import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    doc, 
    getDoc, 
    updateDoc, 
    addDoc, 
    serverTimestamp, 
    getFirestore,
    collection,
    query,
    where,
    getDocs
} from 'firebase/firestore';
import { db } from '../../firebase';
import { ChevronDownIcon, ChevronUpIcon, ArrowLeftIcon, DocumentIcon, BanknotesIcon, PlusIcon, DocumentTextIcon, ClockIcon, CurrencyDollarIcon, MapPinIcon, UserCircleIcon, FunnelIcon, CheckCircleIcon, XMarkIcon, LockOpenIcon, LockClosedIcon } from '@heroicons/react/20/solid';
import PrequalFinancialMetricsDisplay from './PrequalFinancialMetricsDisplay';
import LoanOfficerAssignment from './PrequalLoanOfficerAssignment';
import PrequalificationOcrReview from './PrequalificationOcrReview';
import PrequalDebtServiceCoverageCalculator from './prequalDebtServiceCoverageCalc';
import { generateSummaryPDF } from './PdfGenerator';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import PrequalificationTable from './PrequalificationTable';
import { generatePrequalSummaryPDF } from './PrequalSummaryPdfGenerator';

const PrequalificationDetails = () => {
  const { prequalId } = useParams();
  const navigate = useNavigate();
  const [prequalData, setPrequalData] = useState(null);
  const [ocrData, setOcrData] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isApplicantInfoExpanded, setIsApplicantInfoExpanded] = useState(true);
  const [isFinancialOverviewExpanded, setIsFinancialOverviewExpanded] = useState(true);
  const [isBusinessDetailsExpanded, setIsBusinessDetailsExpanded] = useState(true);
  const [isUploadedFilesExpanded, setIsUploadedFilesExpanded] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isEditingPrequalAmount, setIsEditingPrequalAmount] = useState(false);
  const [newPrequalAmount, setNewPrequalAmount] = useState('');
  const [isSavingAmount, setIsSavingAmount] = useState(false);
  const [isPrequalTableLocked, setIsPrequalTableLocked] = useState(true);

  useEffect(() => {
    const fetchPrequalData = async () => {
      try {
        console.log("Fetching prequalification data for ID:", prequalId);
        
        // Get prequalification application data
        const prequalDocRef = doc(db, 'prequalificationApplications', prequalId);
        const prequalDocSnap = await getDoc(prequalDocRef);
        
        if (prequalDocSnap.exists()) {
          const data = { id: prequalDocSnap.id, ...prequalDocSnap.data() };
          console.log("Found prequalification data:", data);
          setPrequalData(data);
          
          // Get metadata for documents
          const metadataRef = collection(db, 'prequalificationMetadata');
          const metadataQuery = query(metadataRef, 
            where('applicationId', '==', prequalId)
          );
          const metadataSnap = await getDocs(metadataQuery);
          const metadata = metadataSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log("Found metadata:", metadata);
          setUploadedFiles(metadata);
        }
      } catch (error) {
        console.error("Error fetching prequalification data:", error);
      }
    };

    const fetchOCRData = async () => {
      try {
        console.log("Fetching OCR data for prequalId:", prequalId);
        const ocrMetadataRef = collection(db, 'OCRMetadata');
        const q = query(ocrMetadataRef, 
          where('applicationId', '==', prequalId),
          where('documentName', '==', 'Business Tax Returns') // Updated to match the new structure
        );
        const querySnapshot = await getDocs(q);
        
        console.log("OCR query results:", querySnapshot.size);
        
        const processedData = {};
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          console.log("Processing OCR document:", data);
          
          // Extract year from label or filename
          let year = null;
          
          // Try to extract year from filename first
          const yearMatch = data.fileName?.match(/\d{4}/);
          if (yearMatch) {
            year = yearMatch[0];
          }
          
          // If no year in filename, try the label with improved matching
          if (!year && data.label) {
            // Handle various label formats like "2023 Business Tax Return" or "Business Tax Return 2023"
            const labelYearMatch = data.label.match(/\b(20\d{2})\b/);
            if (labelYearMatch) {
              year = labelYearMatch[1];
              console.log(`Extracted year ${year} from label: "${data.label}"`);
            }
          }
          
          // Check for structuredData field first, and use that if available
          if (year) {
            if (data.structuredData) {
              console.log(`Found year ${year} with structured data:`, data.structuredData);
              processedData[year] = data.structuredData;
            } else if (data.ocrResult) {
              console.log(`Found year ${year} with OCR result:`, data.ocrResult);
              processedData[year] = data.ocrResult;
            } else {
              console.log(`No OCR data found for year ${year}`);
            }
          } else {
            console.log(`Could not extract year from document with label: "${data.label}"`);
          }
        });
        
        console.log("Final processed OCR data:", processedData);
        setOcrData(processedData);
      } catch (error) {
        console.error("Error fetching OCR data:", error);
      }
    };

    fetchPrequalData();
    fetchOCRData();
  }, [prequalId]);

  const toggleApplicantInfo = () => setIsApplicantInfoExpanded(!isApplicantInfoExpanded);
  const toggleFinancialOverview = () => setIsFinancialOverviewExpanded(!isFinancialOverviewExpanded);
  const toggleBusinessDetails = () => setIsBusinessDetailsExpanded(!isBusinessDetailsExpanded);
  const toggleUploadedFiles = () => setIsUploadedFilesExpanded(!isUploadedFilesExpanded);

  const handleBack = () => {
    navigate('/admindashboard');
  };

  const handleAssignBank = async (bankId, dropboxLink) => {
    try {
      console.log('Starting bank assignment process...');
      console.log('Prequalification ID:', prequalId);
      console.log('Bank ID:', bankId);
      console.log('Dropbox Link:', dropboxLink);

      const now = new Date().toISOString();

      // Update the prequalification application
      const prequalDocRef = doc(db, 'prequalificationApplications', prequalId);
      const updateData = {
        assignedBankId: bankId,
        status: 'Assigned to Bank',
        lastUpdated: now,
        dropboxLink: dropboxLink
      };

      await updateDoc(prequalDocRef, updateData);
      console.log('Prequalification application updated successfully');

      // Create a new bank assignment
      const bankAssignmentRef = collection(db, 'bankAssignments');
      const newAssignment = {
        prequalificationId: prequalId,
        bankId: bankId,
        assignedAt: now,
        currentStage: {
          name: 'prequalification',
          enteredAt: now
        },
        stageHistory: [
          {
            name: 'prequalification',
            enteredAt: now,
            exitedAt: null
          }
        ],
        dropboxLink: dropboxLink
      };

      const docRef = await addDoc(bankAssignmentRef, newAssignment);
      console.log('Bank assignment created successfully with ID:', docRef.id);

      // Fetch bank data and create email
      const bankRef = doc(db, 'banks', bankId);
      const bankSnap = await getDoc(bankRef);
      if (bankSnap.exists()) {
        const bankData = bankSnap.data();
        console.log('Bank data retrieved:', bankData);
        
        await createEmailDocument(bankData);
      } else {
        console.error('Bank not found:', bankId);
      }

      // Update local state
      setPrequalData(prevData => ({
        ...prevData,
        assignedBankId: bankId,
        status: 'Assigned to Bank',
        lastUpdated: now,
        dropboxLink: dropboxLink
      }));

      console.log('Bank assignment process completed successfully');
    } catch (error) {
      console.error('Error assigning bank:', error);
      throw error;
    }
  };

  const createEmailDocument = async (bankData) => {
    try {
      console.log('Creating email document for bank:', bankData.bankName);
      const emailRef = collection(db, 'email');

      const loginUrl = "https://lended.ai/login"; // Replace with your actual login URL

      const emailData = {
        to: bankData.email,
        from: "max@sbacapital.ai",
        replyTo: "max@sbacapital.ai",
        subject: "You've been matched with a prequalification!",
        templateName: "PrequalMatch",
        message: {
          subject: "You've been matched with a prequalification!",
          firstName: bankData.loanOfficerName,
          prequalDetails: "A new prequalification application has been matched with your bank in the loan portal. Please log in to review the details.",
          html: `<p>Hey ${bankData.loanOfficerName},</p>

<p>A new prequalification application has been matched with your bank in the loan portal. Please <a href="${loginUrl}">log in</a> to review the details.</p>

<p>Looking forward to hearing back from you!</p>

<p>Best,</p>
<p>Max Krakow</p>
<p>Lended</p>
`,
        },
      };
      console.log('Email data prepared:', JSON.stringify(emailData, null, 2));

      const emailDoc = await addDoc(emailRef, emailData);
      console.log('Email document created with ID:', emailDoc.id);
    } catch (error) {
      console.error('Error creating email document:', error);
      console.error('Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      throw error;
    }
  };

  const formatDate = (dateValue) => {
    if (dateValue instanceof Date) {
      return dateValue.toLocaleString();
    } else if (typeof dateValue === 'string') {
      return new Date(dateValue).toLocaleString();
    } else if (dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate().toLocaleString();
    }
    return 'N/A';
  };

  const calculateRevenue = (yearData) => {
    return yearData?.gross_receipts_or_sales || 0;
  };

  const calculateEBITDA = (yearData) => {
    const baseIncome = Number(yearData?.ordinary_business_income || 0);
    const interest = Number(yearData?.interest || 0);
    const depreciation = Number(yearData?.depreciation || 0);
    const amortization = Number(yearData?.amortization || 0);
    return baseIncome + interest + depreciation + amortization;
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return 'N/A';
    
    // For display in the edit form, show the full number
    if (isEditingPrequalAmount) {
      return `$${num.toLocaleString()}`;
    }
    
    // For normal display, show in millions format
    const millions = num / 1000000;
    return `$${millions.toFixed(1)}mm`;
  };

  const handleEditPrequalAmount = () => {
    setNewPrequalAmount(prequalData?.prequalifiedAmount || '');
    setIsEditingPrequalAmount(true);
  };

  const handleSavePrequalAmount = async () => {
    try {
      setIsSavingAmount(true);
      
      // Validate input
      const amount = parseFloat(newPrequalAmount);
      if (isNaN(amount) || amount <= 0) {
        alert('Please enter a valid amount');
        setIsSavingAmount(false);
        return;
      }
      
      // Update in Firestore
      const prequalDocRef = doc(db, 'prequalificationApplications', prequalId);
      await updateDoc(prequalDocRef, {
        prequalifiedAmount: amount,
        lastUpdated: serverTimestamp()
      });
      
      // Update local state
      setPrequalData(prevData => ({
        ...prevData,
        prequalifiedAmount: amount,
        lastUpdated: new Date()
      }));
      
      setIsEditingPrequalAmount(false);
      setIsSavingAmount(false);
    } catch (error) {
      console.error('Error saving prequalified amount:', error);
      alert('Error saving amount. Please try again.');
      setIsSavingAmount(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingPrequalAmount(false);
  };

  const handleGenerateSummaryPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      // Create PDF content with proper defaults for missing values
      const pdfContent = {
        businessName: prequalData.businessName || 'N/A',
        businessDescription: prequalData.businessDescription || '',
        purchasePrice: prequalData.purchasePrice || 0,
        prequalifiedAmount: prequalData.prequalifiedAmount || 0,
        prequalificationType: prequalData.prequalificationType || 'Easy Financing',
        prequalificationWithSalary: prequalData.prequalificationWithSalary || false,
        closingCosts: 10000, // Fixed at ~$10,000
        downPayment: prequalData.purchasePrice ? prequalData.purchasePrice * 0.1 : 0, // 10% of purchase price
        loanTerm: 10, // Fixed at 10 years
        interestRate: prequalData.prequalificationType === 'Easy Financing' ? 9.5 : 10.0, // Prime + 2 or Prime + 2.5
        monthlyPayment: prequalData.monthlyPayment || 0,
        ocrData: ocrData || {},
        documents: uploadedFiles || [],
        businessState: prequalData.businessState || '',
        businessAge: prequalData.businessAge || '',
        projectedSalePrice: prequalData.projectedSalePrice || 0,
        realEstateAppraisal: prequalData.realEstateAppraisal || 0
      };
      
      console.log("Generating PDF with content:", pdfContent);
      
      // Generate PDF with error handling
      let pdfBlob;
      try {
        pdfBlob = await generatePrequalSummaryPDF(pdfContent);
      } catch (pdfError) {
        console.error('Error in PDF generation:', pdfError);
        setIsGeneratingPDF(false);
        alert('Error generating PDF. Please check console for details.');
        return;
      }
      
      if (!pdfBlob) {
        console.error('PDF generation returned empty blob');
        setIsGeneratingPDF(false);
        alert('Error: PDF generation failed to produce a document.');
        return;
      }
      
      // Upload PDF to Firebase Storage
      const fileName = `${prequalData.businessName || 'Business'}_Prequalification_Summary_${new Date().toISOString().replace(/:/g, '-')}.pdf`;
      const storageRef = ref(storage, `prequalifications/${prequalId}/summaries/${fileName}`);
      
      const uploadTask = uploadBytesResumable(storageRef, pdfBlob);
      
      uploadTask.on('state_changed', 
        (snapshot) => {
          // Progress monitoring
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('Upload is ' + progress + '% done');
        }, 
        (error) => {
          console.error('Error uploading PDF:', error);
          setIsGeneratingPDF(false);
          alert('Error uploading PDF. Please try again.');
        }, 
        async () => {
          try {
            // Upload completed successfully
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            // Add the PDF to the documents array in Firestore
            const prequalDocRef = doc(db, 'prequalificationApplications', prequalId);
            const newDocument = {
              id: Date.now().toString(),
              fileName: fileName,
              fileUrl: downloadURL,
              fileType: 'application/pdf',
              uploadDate: new Date().toISOString(),
              documentType: 'Prequalification Summary'
            };
            
            // Update the documents array in Firestore with error handling
            try {
              if (prequalData.documents && Array.isArray(prequalData.documents)) {
                await updateDoc(prequalDocRef, {
                  documents: [...prequalData.documents, newDocument]
                });
                setUploadedFiles([...prequalData.documents, newDocument]);
              } else if (prequalData.documentMetadata && Array.isArray(prequalData.documentMetadata)) {
                await updateDoc(prequalDocRef, {
                  documentMetadata: [...prequalData.documentMetadata, newDocument]
                });
                setUploadedFiles([...prequalData.documentMetadata, newDocument]);
              } else {
                await updateDoc(prequalDocRef, {
                  documents: [newDocument]
                });
                setUploadedFiles([newDocument]);
              }
            } catch (firestoreError) {
              console.error('Error updating Firestore:', firestoreError);
              // Still open the PDF even if Firestore update fails
            }
            
            // Open the PDF in a new tab
            window.open(downloadURL, '_blank');
          } catch (finalError) {
            console.error('Error in final processing:', finalError);
          } finally {
            setIsGeneratingPDF(false);
          }
        }
      );
    } catch (error) {
      console.error('Error in overall PDF generation process:', error);
      setIsGeneratingPDF(false);
      alert('An error occurred. Please try again.');
    }
  };

  const togglePrequalTableLock = () => {
    setIsPrequalTableLocked(!isPrequalTableLocked);
  };

  const handleRequestUnlock = () => {
    // Scroll to the prequalification table section
    const prequalTableElement = document.getElementById('prequalification-table');
    if (prequalTableElement) {
      prequalTableElement.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Flash the unlock button to draw attention
    const lockButton = document.getElementById('toggle-lock-button');
    if (lockButton) {
      lockButton.classList.add('animate-pulse');
      setTimeout(() => {
        lockButton.classList.remove('animate-pulse');
      }, 2000);
    }
  };

  if (!prequalData) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-gray-300 h-12 w-12"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-300 rounded"></div>
              <div className="h-4 bg-gray-300 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentYear = Object.keys(ocrData).sort().pop();
  const currentYearData = ocrData[currentYear];

  const financialStats = [
    { name: 'Revenue', value: calculateRevenue(currentYearData), prefix: '$' },
    { name: 'EBITDA', value: calculateEBITDA(currentYearData), prefix: '$' },
    { name: 'Projected Sale Price', value: prequalData.projectedSalePrice, prefix: '$' },
    { name: 'Real Estate Appraisal', value: prequalData.realEstateAppraisal, prefix: '$' },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <main className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header with back button and PDF generation */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                Prequalification Details
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                View and manage prequalification information for {prequalData.businessName || 'this business'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleBack}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Admin Dashboard
              </button>
              
              <button
                onClick={handleGenerateSummaryPDF}
                disabled={isGeneratingPDF}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
              >
                {isGeneratingPDF ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    Generate Summary PDF
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Prequalification Amount Card */}
          <div className="bg-gradient-to-br from-white to-green-50 shadow-lg rounded-xl overflow-hidden mb-8 border border-green-100">
            <div className="px-6 py-8 sm:px-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold leading-7 text-gray-900 flex items-center">
                    <BanknotesIcon className="h-6 w-6 text-indigo-600 mr-2" />
                    {prequalData.businessName || 'Business'} 
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Prequalification Status: <span className={`font-medium ${
                      prequalData.status === 'Complete' ? 'text-green-600' :
                      prequalData.status === 'In progress' ? 'text-blue-600' :
                      prequalData.status === 'Assigned to Bank' ? 'text-purple-600' :
                      'text-gray-600'
                    }`}>
                      {prequalData.status || 'Not started'}
                    </span>
                  </p>
                </div>
                
                <div className="mt-4 sm:mt-0">
                  {isEditingPrequalAmount ? (
                    <div className="bg-white rounded-lg shadow-sm border border-green-200 px-6 py-4">
                      <p className="text-sm text-gray-500 mb-1">Edit Prequalified Amount</p>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={newPrequalAmount}
                          onChange={(e) => setNewPrequalAmount(e.target.value)}
                          className="block w-48 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          placeholder="Enter amount"
                          disabled={isSavingAmount}
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={handleSavePrequalAmount}
                            disabled={isSavingAmount}
                            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                          >
                            {isSavingAmount ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={isSavingAmount}
                            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center sm:justify-end">
                      <div className="bg-white rounded-lg shadow-sm border border-green-200 px-6 py-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-500 mb-1">Prequalified Amount</p>
                          <button
                            onClick={handleEditPrequalAmount}
                            className="ml-4 inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Edit
                          </button>
                        </div>
                        <p className="text-3xl font-bold text-green-600">
                          {prequalData.prequalifiedAmount 
                            ? `$${Number(prequalData.prequalifiedAmount).toLocaleString()}` 
                            : (prequalData.expectedPrequalAmount 
                                ? formatCurrency(prequalData.expectedPrequalAmount) 
                                : 'Not calculated yet')}
                        </p>
                        {prequalData.prequalifiedAmount && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                              {prequalData.prequalificationType || 'Standard Financing'}
                            </span>
                            {prequalData.prequalificationWithSalary && (
                              <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-700/10">
                                With $75k Salary
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Business Details */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Business Details</h3>
                </div>
                <button
                  onClick={toggleBusinessDetails}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isBusinessDetailsExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isBusinessDetailsExpanded && (
                <div className="border-t border-gray-100 px-6 py-5">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Business name</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-medium">
                        {prequalData.businessName || 'N/A'}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Description</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {prequalData.businessDescription || 'No description provided.'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-1" />
                        Purchase price
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 font-medium">
                        {prequalData.purchasePrice ? `$${Number(prequalData.purchasePrice).toLocaleString()}` : 'N/A'}
                        {prequalData.realEstateIncluded && prequalData.purchasePrice && prequalData.realEstateValue &&
                        (() => {
                          const pp = parseFloat(prequalData.purchasePrice);
                          const rev = parseFloat(prequalData.realEstateValue);
                          if (!isNaN(pp) && !isNaN(rev)) {
                            return (
                              <div className="mt-1 pl-4 text-xs text-gray-700">
                                <p>Business Acquisition (Est.): {`$${(pp - rev).toLocaleString()}`}</p>
                                <p>Included Real Estate: {`$${rev.toLocaleString()}`}</p>
                              </div>
                            );
                          }
                          return null; 
                        })()}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Business Age</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {prequalData.businessAge || 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">License Needed</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData.licenseNeeded === 'boolean' ? (prequalData.licenseNeeded ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Real Estate Included</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData.realEstateIncluded === 'boolean' ? (prequalData.realEstateIncluded ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                    {prequalData.realEstateIncluded && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Real Estate Value</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {prequalData.realEstateValue ? `$${Number(prequalData.realEstateValue).toLocaleString()}` : 'N/A'}
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Has Multiple LLCs</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData.hasMultipleLLCs === 'boolean' ? (prequalData.hasMultipleLLCs ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Has Other Addbacks</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData.hasOtherAddbacks === 'boolean' ? (prequalData.hasOtherAddbacks ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Top Customer Concentration</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData.topCustomerConcentration === 'boolean' ? (prequalData.topCustomerConcentration ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Created At</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {prequalData.createdAt ? formatDate(prequalData.createdAt) : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {prequalData.lastUpdated ? formatDate(prequalData.lastUpdated) : 'N/A'}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>

            {/* Financial Overview */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <BanknotesIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Financial Overview</h3>
                </div>
                <button
                  onClick={toggleFinancialOverview}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isFinancialOverviewExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isFinancialOverviewExpanded && (
                <div className="border-t border-gray-100 px-6 py-5">
                  {Object.keys(ocrData).length > 0 ? (
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <PrequalFinancialMetricsDisplay ocrData={ocrData} loanData={prequalData} />
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <BanknotesIcon className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500 italic">No financial data available for this prequalification.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Prequalification Table */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100" id="prequalification-table">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <FunnelIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Prequalification Analysis</h3>
                </div>
                <button
                  id="toggle-lock-button"
                  onClick={togglePrequalTableLock}
                  className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white ${isPrequalTableLocked ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-green-600 hover:bg-green-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 ${isPrequalTableLocked ? 'focus:ring-indigo-500' : 'focus:ring-green-500'}`}
                >
                  {isPrequalTableLocked ? (
                    <>
                      <LockOpenIcon className="h-4 w-4 mr-1" />
                      Unlock Selection
                    </>
                  ) : (
                    <>
                      <LockClosedIcon className="h-4 w-4 mr-1" />
                      Lock Selection
                    </>
                  )}
                </button>
              </div>
              <div className="border-t border-gray-100 px-6 py-5 overflow-x-auto">
                <PrequalificationTable 
                  ocrData={ocrData} 
                  loanData={prequalData || {}}
                  isLocked={isPrequalTableLocked}
                  onUnlockRequest={handleRequestUnlock}
                />
              </div>
            </div>

            {/* Uploaded Files */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <DocumentIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Uploaded Files</h3>
                </div>
                <button
                  onClick={toggleUploadedFiles}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isUploadedFilesExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isUploadedFilesExpanded && (
                <div className="border-t border-gray-100">
                  {uploadedFiles && uploadedFiles.length > 0 ? (
                    <div className="p-6">
                      {/* Sort and group files by documentName and then by label */}
                      {(() => {
                        // Sort files by documentName first, then by label
                        const sortedFiles = [...uploadedFiles].sort((a, b) => {
                          // First sort by documentName
                          const docNameA = (a.documentName || '').toLowerCase();
                          const docNameB = (b.documentName || '').toLowerCase();
                          
                          if (docNameA !== docNameB) {
                            return docNameA.localeCompare(docNameB);
                          }
                          
                          // If document names are equal, sort by label
                          const labelA = (a.label || '').toLowerCase();
                          const labelB = (b.label || '').toLowerCase();
                          return labelA.localeCompare(labelB);
                        });
                        
                        // Group files by documentName
                        const groupedByDocName = sortedFiles.reduce((groups, file) => {
                          const docName = file.documentName || 'Other Documents';
                          if (!groups[docName]) {
                            groups[docName] = [];
                          }
                          groups[docName].push(file);
                          return groups;
                        }, {});
                        
                        // For each documentName group, further group by label
                        const groupedFiles = {};
                        Object.entries(groupedByDocName).forEach(([docName, files]) => {
                          groupedFiles[docName] = files.reduce((labelGroups, file) => {
                            const label = file.label || 'Unlabeled';
                            if (!labelGroups[label]) {
                              labelGroups[label] = [];
                            }
                            labelGroups[label].push(file);
                            return labelGroups;
                          }, {});
                        });
                        
                        // Render grouped files
                        return Object.entries(groupedFiles).map(([docName, labelGroups]) => (
                          <div key={docName} className="mb-8">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-gray-200">
                              {docName}
                            </h3>
                            
                            {Object.entries(labelGroups).map(([label, files]) => (
                              <div key={`${docName}-${label}`} className="mb-6">
                                <h4 className="text-md font-medium text-gray-700 mb-3 pl-2 border-l-4 border-indigo-400">
                                  {label}
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  {files.map((file, index) => (
                                    <div 
                                      key={file.id || file.fileId || `${docName}-${label}-${index}`} 
                                      className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors border border-gray-200"
                                    >
                                      <div className="flex items-start space-x-3">
                                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-indigo-50 rounded-lg">
                                          <DocumentIcon className="h-5 w-5 text-indigo-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 truncate">
                                            {file.fileName || file.name || 'Unnamed File'}
                                          </p>
                                          <p className="text-xs text-gray-500 mt-1">
                                            {formatDate(file.uploadedAt || file.createdAt || '')}
                                          </p>
                                          <div className="mt-2">
                                            <a
                                              href={file.fileUrl || file.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                                            >
                                              View Document
                                            </a>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ));
                      })()}
                    </div>
                  ) : (
                    <div className="px-6 py-8 text-center">
                      <DocumentIcon className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500 italic">No files uploaded yet.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* OCR Review */}
            {prequalId && (
              <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
                <div className="px-6 py-5 bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-indigo-600 mr-2" />
                    <h3 className="text-lg font-semibold leading-7 text-gray-900">OCR Document Review</h3>
                  </div>
                </div>
                <div className="border-t border-gray-100">
                  <PrequalificationOcrReview 
                    prequalId={prequalId} 
                    uploadedFiles={uploadedFiles}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrequalificationDetails;